import { match, P } from "ts-pattern";
import { z, type ZodType } from "zod";

import type { FormBuilderField } from "../../../types/fields.js";
import { FormSectionSchema } from "../../../types/FormSection.js";
import type {
  BuildFormSpecificationProps,
  RecordPolicePresenceMinimumFields,
  RequestPolicePresenceMinimumFields,
} from "../../../types/workflow/index.js";

type RecordPolicePresenceFormSpecification<
  DataType extends RecordPolicePresenceMinimumFields,
> = (props: BuildFormSpecificationProps) => {
  strictFieldSchemas: Record<string, ZodType>;
  formSchema: ZodType<DataType>;
  pages: {
    fields: readonly FormBuilderField<
      DataType,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any
    >[];
  }[];
  defaultValues: Partial<DataType>;
};

export function buildRecordPolicePresenceFormSpecification<
  DataType extends RecordPolicePresenceMinimumFields,
>(
  recordPolicePresenceFormSpecification: RecordPolicePresenceFormSpecification<DataType>,
  recordPolicePresenceOfficerResponseNoFormSpecification: RecordPolicePresenceFormSpecification<DataType>,
  recordPolicePresenceOfficerResponseYesFormSpecification: RecordPolicePresenceFormSpecification<DataType>,
) {
  return (props: BuildFormSpecificationProps) => {
    const form = props.incidentWorkItem?.forms.find(
      (form) => form.id === props.formId,
    );

    const requestSection = form?.sections.find(
      (section) =>
        section.id === FormSectionSchema.enum.requestPolicePresenceMain,
    );

    const requestSectionData = requestSection
      ? (requestSection.data as RequestPolicePresenceMinimumFields)
      : null;

    return match([
      requestSectionData?.source?.value,
      requestSectionData?.hasPolicePresenceRequested.value,
    ])
      .with([P.nullish, P.any], () =>
        recordPolicePresenceFormSpecification(props),
      )
      .with([P.any, P.nullish], () =>
        recordPolicePresenceFormSpecification(props),
      )
      .with(["emha", P.any], () => recordPolicePresenceFormSpecification(props))
      .with([P.any, "yes"], () =>
        recordPolicePresenceOfficerResponseYesFormSpecification(props),
      )
      .with([P.any, P.union("no", "already-left")], () =>
        recordPolicePresenceOfficerResponseNoFormSpecification(props),
      )
      .exhaustive();
  };
}

export const hasRemainedAtPlaceOfSafetySchema = z.enum([
  "yes" as const,
  "no" as const,
  "no-escalated-and-left" as const,
  "yes-stayed-agree-with-nhs" as const,
  "yes-stayed-disagree-with-nhs" as const,
]);
